import { USER_API_URL } from "../config";

interface AuthResponse {
  token: string;
  error?: string;
}

interface AuthRequest {
  email: string;
  password: string;
}

export interface SubscriptionStatus {
  customerID: string | null;
  hasSubscription: boolean;
  planName?: string;
  status?: string;
  currentPeriodEnd?: number;
}

interface CheckoutSessionResponse {
  url: string;
}

interface AuthCheckResponse {
  authenticated: boolean;
  user?: {
    id: string;
    email: string;
  };
}

interface SignupEmailRequest {
  email: string;
}

interface SetPasswordRequest {
  token: string;
  password: string;
}

interface PasswordResetRequest {
  email: string;
}

interface PasswordResetResponse {
  message: string;
}

export async function login(credentials: AuthRequest): Promise<AuthResponse> {
  try {
    const response = await fetch(`${USER_API_URL}/api/user/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Login failed");
    }

    return data;
  } catch (error) {
    throw error;
  }
}

export async function signup(
  credentials: SignupEmailRequest
): Promise<{ conflict: boolean }> {
  const response = await fetch(`${USER_API_URL}/api/user/signup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(credentials),
  });

  if (response.status === 409) {
    return { conflict: true };
  }

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || "Signup failed");
  }

  return { conflict: false };
}

export async function setPassword(
  request: SetPasswordRequest
): Promise<AuthResponse> {
  const response = await fetch(`${USER_API_URL}/api/user/set-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Failed to set password");
  }

  return data;
}

export async function checkIsWhitelisted(email: string): Promise<boolean> {
  const response = await fetch(`${USER_API_URL}/api/user/check-whitelist`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error("Failed to check whitelist");
  }
  return await response.json();
}

export async function checkSubscription(): Promise<SubscriptionStatus> {
  const token = localStorage.getItem("authToken");

  const response = await fetch(`${USER_API_URL}/api/subscription/check`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to check subscription status");
  }

  return await response.json();
}

export async function createCheckoutSession(
  customerID: string | null,
  includeTrial: boolean
): Promise<CheckoutSessionResponse> {
  const token = localStorage.getItem("authToken");

  const response = await fetch(
    `${USER_API_URL}/api/subscription/create-checkout-session`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        customerID,
        includeTrial,
      }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to create checkout session");
  }

  return await response.json();
}

export async function getManageSubscriptionUrl(): Promise<CheckoutSessionResponse> {
  const token = localStorage.getItem("authToken");

  const response = await fetch(
    `${USER_API_URL}/api/subscription/billing-portal`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to get billing portal URL");
  }

  return await response.json();
}

export async function checkAuth(): Promise<AuthCheckResponse> {
  const token = localStorage.getItem("authToken");

  try {
    const response = await fetch(`${USER_API_URL}/api/user/check-auth`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return { authenticated: false };
    }

    return await response.json();
  } catch (error) {
    return { authenticated: false };
  }
}

export const requestPasswordReset = async (
  request: PasswordResetRequest
): Promise<boolean> => {
  const response = await fetch(`${USER_API_URL}/api/user/reset-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to request password reset");
  }

  return true;
};

export function logout(): void {
  localStorage.removeItem("authToken");
}

export const checkIsExtensionInstalled = async (): Promise<boolean> => {
  try {
    // @ts-ignore
    await chrome.runtime.sendMessage("jchflalokogblejikeiajkfolhobodbg", {
      message: "is-installed",
    });
    return true;
  } catch (err) {
    return false;
  }
};
